import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import { User } from '@data/models/user.interface';
import {
  LDClient,
  LDContext,
  LDFlagSet,
  initialize,
} from 'launchdarkly-react-client-sdk';

interface LaunchDarklyContextValue {
  flags: LDFlagSet;
  isReady: boolean;
  client: LDClient | null;
}

const LaunchDarklyContext = createContext<LaunchDarklyContextValue>({
  flags: {},
  isReady: false,
  client: null,
});

interface LaunchDarklyProviderProps {
  children: ReactNode;
  user?: User;
}

const getNinaRoles = (roles: string[]): string[] => [
  ...new Set(
    roles
      .filter((role) => role.startsWith('nina.'))
      .map((role) => role.replace('nina.', '').replace(/_/g, ' ')),
  ),
];

const getNinaScopes = (scopes: string[]): string[] => [
  ...new Set(
    scopes
      .filter((scope) => scope.startsWith('nina.'))
      .map((scope) => scope.replace('nina.', '')),
  ),
];

export const LaunchDarklyProvider = ({
  children,
  user,
}: LaunchDarklyProviderProps): JSX.Element => {
  const [client, setClient] = useState<LDClient | null>(null);
  const [flags, setFlags] = useState<LDFlagSet>({});
  const [isReady, setIsReady] = useState(false);
  useEffect(() => {
    const clientSideId = import.meta.env.VITE_APP_LAUNCHDARKLY_CLIENT_ID;

    if (!clientSideId) {
      console.warn(
        'LaunchDarkly client ID not configured. Feature flags will use default values.',
      );
      setIsReady(true);
      return;
    }

    const context: LDContext = user?.email
      ? {
          kind: 'user',
          key: user.email,
          email: user.email,
          name: user.fullname,
          code_analytique_paie: getNinaScopes(user.scopes),
          roles: getNinaRoles(user.roles),
        }
      : {
          kind: 'user',
          anonymous: true,
          key: 'anonymous-user',
        };

    const ldClient = initialize(clientSideId, context);

    ldClient.on('ready', () => {
      const allFlags = ldClient.allFlags();
      setFlags(allFlags);
      setIsReady(true);
    });

    ldClient.on('change', () => {
      setFlags(ldClient.allFlags());
    });

    setClient(ldClient);

    return () => {
      ldClient.close();
    };
  }, [user?.id, user?.email, user?.fullname]);

  return (
    <LaunchDarklyContext.Provider value={{ flags, isReady, client }}>
      {children}
    </LaunchDarklyContext.Provider>
  );
};

export const useLaunchDarkly = (): LaunchDarklyContextValue => {
  return useContext(LaunchDarklyContext);
};

export const useFeatureFlag = <T = boolean,>(
  flagKey: string,
  defaultValue?: T,
): T => {
  const { flags, isReady } = useLaunchDarkly();

  if (!isReady) {
    return defaultValue as T;
  }

  return (flags[flagKey] as T) ?? (defaultValue as T);
};
